import { Link } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { usePhaseStatus } from '../lib/api'
import { useAuth } from '../lib/auth'

function fmtEur(v: string | number): string {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
}

const TIER_PRICE: Record<string, number> = { starter: 49, pro: 149, elite: 399 }

export function BillingPage() {
  const { account } = useAuth()
  const ps = usePhaseStatus()
  const tierCode = account?.tierCode ?? 'pro'
  // Only count a charge if the trader actually has a challenge.
  const spent = account ? (TIER_PRICE[tierCode] ?? 0) : 0
  const earned = 0

  // Progress derivations — mirror PhaseView so the Billing summary matches the
  // dashboard card exactly.
  const profitTargetEur = ps.data ? Number(ps.data.profitTargetEur) : 0
  const currentBalance  = ps.data ? Number(ps.data.balance) : 0
  const profitRemaining = profitTargetEur > 0 ? Math.max(0, profitTargetEur - currentBalance) : 0
  const profitPct       = ps.data ? Math.max(0, Math.min(100, ps.data.profitProgressPct)) : 0
  const daysRemaining   = ps.data?.daysRemaining ?? null
  const totalDays       = ps.data?.totalDays ?? null
  const daysElapsed     = daysRemaining != null && totalDays != null ? Math.max(0, totalDays - daysRemaining) : null
  const timePct         = daysElapsed != null && totalDays != null && totalDays > 0
    ? Math.max(0, Math.min(100, (daysElapsed / totalDays) * 100))
    : 0

  return (
    <AppShell page="billing" breadcrumb={<span className="topbar__breadcrumb-current">Billing</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Account</span></p>
          <h1 className="page-header__title">Billing</h1>
          <p className="page-header__subtitle">Track challenge purchases, payouts, and manage payment methods.</p>
        </div>
      </header>

      <nav className="tabs" aria-label="Billing sections">
        <button type="button" className="tab tab--active">Overview</button>
        <button type="button" className="tab" disabled title="Available once the production payment provider is wired up." style={{ opacity: 0.5, cursor: 'not-allowed' }}>Charges</button>
        <button type="button" className="tab" disabled title="Available once the production payment provider is wired up." style={{ opacity: 0.5, cursor: 'not-allowed' }}>Payouts</button>
        <button type="button" className="tab" disabled title="Available once the production payment provider is wired up." style={{ opacity: 0.5, cursor: 'not-allowed' }}>Payment methods</button>
      </nav>

      <div className="dashboard-grid dashboard-grid--cols-3"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)', margin: 'var(--space-4) 0 var(--space-5)' }}>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Total spent</span></header>
          <div className="kpi-card__value">{fmtEur(spent)}</div>
          <p className="kpi-card__detail">{account ? '1 challenge · current tier' : 'No charges yet'}</p>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Total earned</span></header>
          <div className="kpi-card__value">{fmtEur(earned)}</div>
          <p className="kpi-card__detail">Payouts unlock after Phase 3</p>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Net</span></header>
          <div
            className="kpi-card__value"
            style={{ color: (spent - earned) > 0 ? 'var(--color-danger-text)' : (spent - earned) < 0 ? 'var(--color-success-text)' : undefined }}
          >
            {(spent - earned) > 0 ? '−' : ''}{fmtEur(Math.abs(spent - earned))}
          </div>
          <p className="kpi-card__detail">Lifetime balance</p>
        </article>
      </div>

      {account && (
        <section className="billing-section">
          <header className="billing-section__header">
            <h2 className="billing-section__title">Active challenge</h2>
          </header>
          <div className="active-challenge">
            <div className="active-challenge__main">
              <div className="active-challenge__title-row">
                <span className="active-challenge__title">
                  {tierCode.charAt(0).toUpperCase() + tierCode.slice(1)} Challenge — {account.currentPhase.toUpperCase()}
                </span>
                <span className="tx-status tx-status--paid">Active</span>
              </div>
              <div className="active-challenge__meta">
                <span>Starting balance <span className="active-challenge__meta-value">{fmtEur(account.startingBalance)}</span></span>
                <span>·</span>
                <span>Current <span className="active-challenge__meta-value">{fmtEur(account.currentBalance)}</span></span>
              </div>
              {ps.data && totalDays != null && daysRemaining != null && (
                <div className="active-challenge__progress-section">
                  <div className="active-challenge__progress-row">
                    <span className="active-challenge__progress-label">Time</span>
                    <span className="active-challenge__progress-meta">
                      Day {daysElapsed ?? 0} of {totalDays} · {daysRemaining} left
                    </span>
                  </div>
                  <div className="active-challenge__progress-bar">
                    <div className="active-challenge__progress-fill" style={{ width: `${timePct}%`, background: 'var(--color-primary)' }} />
                  </div>
                  <div className="active-challenge__progress-row" style={{ marginTop: 'var(--space-3)' }}>
                    <span className="active-challenge__progress-label">Profit target</span>
                    <span className="active-challenge__progress-meta">
                      +{ps.data.profitTargetPct}% · {fmtEur(profitRemaining)} remaining
                    </span>
                  </div>
                  <div className="active-challenge__progress-bar">
                    <div className="active-challenge__progress-fill" style={{ width: `${profitPct}%`, background: 'var(--color-success)' }} />
                  </div>
                </div>
              )}
            </div>
            <div className="active-challenge__actions">
              <Link to="/" className="btn btn--secondary btn--sm">View dashboard</Link>
            </div>
          </div>
        </section>
      )}

      <div className="coming-soon" style={{ marginTop: 'var(--space-5)' }}>
        <div className="coming-soon__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="6" width="18" height="13" rx="2" />
            <path d="M3 10 h18 M7 15 h4" />
          </svg>
        </div>
        <div>
          <div className="coming-soon__title">Detailed charges, payouts and payment methods coming soon</div>
          <div className="coming-soon__body">
            Invoice list, payout queue and card management arrive once the production payment provider is finalized.
            For now the challenge checkout runs in mock mode.
          </div>
        </div>
      </div>
    </AppShell>
  )
}
